import { Box, Table, Text, Tag } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import WaitingTimePerServiceLineGraph from "../charts/WaitingTimePerServiceLineGraph";
import ConfiguredReportTable from "../../reports/ConfiguredReportTable";
import { formatTime, formatNumber } from "../../../store/helpers";
import {
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "../../../store/renderConfig";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";

const SERVICE_NAME_COLUMN = { id: "serviceName", label: "Service", pinned: true };

const getWaitingTimePerServiceRows = ({ data = [], branchName = "", scope = "" }) =>
  (data || [])
    .filter((service) => Number(service?.completed))
    .map((service) => ({
      ...service,
      serviceName: service.name || "",
      branchName,
      scope,
      totalWaitingTime: service.waitingTimeInSeconds,
      average: service.avg,
    }));

const getVisibleColumns = () =>
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS.filter((column) => column.id !== "serviceName");

const renderCell = (serviceData, columnId) => {
  if (columnId === "serviceName") return serviceData.serviceName || serviceData.name;
  if (columnId === "completed") return <Text color="var(--blue)">{formatNumber(serviceData.completed)}</Text>;
  if (columnId === "totalWaitingTime") return <Text color="var(--blue)">{formatTime(serviceData.waitingTimeInSeconds)}</Text>;
  if (columnId === "average") return formatTime(serviceData.avg);
  if (columnId === "min") return formatTime(serviceData.min);
  if (columnId === "max") return formatTime(serviceData.max);
  return null;
};

const WaitingTimePerServiceTable = ({
  branchId = null,
  data,
  reportIndex,
  branchIndex,
  numServices,
  branchName,
}) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const { showEmptyDataSets } = useSelector((state) => state.defaultReducer);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const chartTitle = branchId ? `${branchName} Branch, ` : "Entire Organization,";
  const branchTitle = branchId
    ? `${reportIndex}. ${branchIndex ? `${branchIndex}. ` : ""}${branchName} Branch`
    : "Entire Organization";
  const rows = sortReportRows(
    getWaitingTimePerServiceRows({
      data,
      branchName: branchName || "",
      scope: branchId ? "Branch" : "Organization",
    }),
    config,
    { columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS }
  );
  const visibleColumns = getVisibleColumns();
  const visibleChartRows = (data || []).filter(
    (item) => showEmptyDataSets || (!showEmptyDataSets && Number(item.completed) > 0)
  );

  return (
    <Box mt={10}>
      <Box mb="20px">
        <h2>{branchId ? branchTitle : `${reportIndex}. 1. Entire Organization`}</h2>
        {branchId ? <p>{numServices} services.</p> : null}
      </Box>

      {!config.charts.enabled ? (
        <Box className="reportTableContainer waitingTimePerServiceBlock">
          <ConfiguredReportTable
            pinnedColumns={[SERVICE_NAME_COLUMN]}
            columns={visibleColumns}
            rows={rows}
            renderConfig={config}
            renderCell={renderCell}
            presortedRows={true}
          />
        </Box>
      ) : null}

      {config.charts.enabled
        ? visibleChartRows.map((serviceData, index) => (
            <Box key={index} className="reportContainer waitingTimePerServiceBlock" mt="20px">
              <Box mb="10px">
                <h3>
                  {reportIndex}. {branchIndex ? `${branchIndex}. ` : null}
                  {index + 1}. {serviceData.name}
                </h3>
                {branchId ? (
                  <Tag.Root>
                    <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                  </Tag.Root>
                ) : null}
              </Box>
              <Table.Root className="stripedTable" size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">Total Completed Tickets</Table.ColumnHeader>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">Total Waiting Time</Table.ColumnHeader>
                    <Table.ColumnHeader>Avg /ticket.</Table.ColumnHeader>
                    <Table.ColumnHeader>Min.</Table.ColumnHeader>
                    <Table.ColumnHeader>Max</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell borderRight="1px solid #dadce0">{renderCell(serviceData, "completed")}</Table.Cell>
                    <Table.Cell borderRight="1px solid #dadce0">{renderCell(serviceData, "totalWaitingTime")}</Table.Cell>
                    <Table.Cell>{renderCell(serviceData, "average")}</Table.Cell>
                    <Table.Cell>{renderCell(serviceData, "min")}</Table.Cell>
                    <Table.Cell>{renderCell(serviceData, "max")}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan={9} p={0}>
                      <Box mt="20px" mb="20px">
                        <WaitingTimePerServiceLineGraph
                          title={`${serviceData.name}: ${chartTitle} Day by Day Comparison`}
                          chart={serviceData.days}
                          chartHeight="200px"
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </Box>
          ))
        : null}
      {!rows.length && !config.charts.enabled ? (
        <Text fontSize="sm">No data available for this report</Text>
      ) : null}
    </Box>
  );
};

export default WaitingTimePerServiceTable;
